import { Injectable } from '@angular/core';
import { get, push, ref, remove } from 'firebase/database';
import { eventWithTime, record } from 'rrweb';

import { UserProfileStateService } from './user-profile.state.service';
import { environment } from '../../../environments/environment';
import { database } from '../../firebase/firebase';
import { ISnapshotRecord } from '../../shared/models/common.model';
import { IUserProfileModel } from '../../shared/models/user.model';


@Injectable({
  providedIn: 'root'
})
export class UserInsightStateService {

  private eventsLog: eventWithTime[] = [];
  private stopRecording?: () => void;

  private backgroundEvents: eventWithTime[] = [];
  private pendingEvents: eventWithTime[] = [];
  private stopBackgroundRecording?: () => void;
  private flushTimer?: ReturnType<typeof setInterval>;

  private isRecording = false;
  private userId = '';

  constructor(private readonly userProfileStateService: UserProfileStateService) {
    this.userProfileStateService.getState().subscribe({
      next: (user: IUserProfileModel) => {
        this.userId = user.nameId && user.nameId != '' ? user.nameId : environment.firebaseConfig.userMasterConfig.SignOrGuestUser;
      },
    })
  }

  start(): void {
    this.stop();

    this.eventsLog = [];

    this.stopRecording = record({
      emit: (event) => this.eventsLog.push(event)
    });

    record.takeFullSnapshot();
  }

  getSnapshot(): string {
    return JSON.stringify(this.eventsLog);
  }

  stop(): void {
    this.stopRecording?.();
    this.stopRecording = undefined;
  }

  reset(): void {
    this.stop();
    this.eventsLog = [];
  }

  startBackground(): void {
    if (!environment.firebaseConfig.userMasterConfig.enabled) {
      return;
    }

    if (this.isRecording) {
      return;
    }
    try {
      this.backgroundEvents = [];
      this.pendingEvents = [];
      this.stopBackgroundRecording = record({
        emit: (event) => {
          this.backgroundEvents.push(event);
          this.pendingEvents.push(event);
        }
      });
      record.takeFullSnapshot();

      this.isRecording = true;
      this.flushTimer = setInterval(() => {
        void this.flushPendingEvents();
      }, environment.firebaseConfig.userMasterConfig.intervalMinutes * 60 * 1000);
    } catch (error) {
      console.error('Unable to handle snap', error);
    }
  }

  stopBackground(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = undefined;
    }

    this.stopBackgroundRecording?.();
    this.stopBackgroundRecording = undefined;

    this.isRecording = false;
    if (this.pendingEvents.length > 0) {
      void this.flushPendingEvents(true);
    }
  }

  async flushPendingEvents(force = false): Promise<void> {
    if (!environment.firebaseConfig.userMasterConfig.enabled) {
      return;
    }

    if (!force && this.pendingEvents.length === 0) {
      return;
    }

    try {
      const basePath = `${environment.firebaseConfig.userMasterConfig.collectionName}/${this.userId}/snapshots`;
      const userRef = ref(database, basePath);
      const payload = {
        createdOn: Date.now(),
        snap: JSON.stringify(this.pendingEvents)
      };

      await push(userRef, payload);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const records: ISnapshotRecord[] = [];
        snapshot.forEach(child => {
          records.push({
            key: child.key!,
            createdOn: child.val().createdOn
          });
        });
        records.sort((a, b) => a.createdOn - b.createdOn);
        const maxRecords = environment.firebaseConfig.userMasterConfig.maxRecordsPerUser * (environment.firebaseConfig.userMasterConfig.SignOrGuestUser == this.userId ? 5 :1);

        while (records.length > maxRecords) {
          const oldest = records.shift();
          if (oldest) {
            await remove(ref(database, `${basePath}/${oldest.key}`));
          }
        }
      }

      this.stopBackgroundRecording?.();
      this.stopBackgroundRecording = undefined;

      if (this.flushTimer) {
        clearInterval(this.flushTimer);
        this.flushTimer = undefined;
      }

      this.isRecording = false;
      this.backgroundEvents = [];
      this.pendingEvents = [];
    } catch (error) {
      console.error('Firebase Upload Error:', error);
    }
  }
}